import React, { useState } from 'react';
import axios from 'axios';
import { Rocket, Loader2, CheckCircle2, ExternalLink, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DeployCloudflareModal({
  open,
  onOpenChange,
  sessionId,
  projectName,
  hasProject,
  isAuthed,
  onLogin,
}) {
  const [name, setName] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [result, setResult] = useState(null);

  // Sensible default
  React.useEffect(() => {
    if (open) setResult(null);
    if (projectName && !name) {
      const slug = projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setName(slug || 'transformerus-app');
    }
  }, [open, projectName, name]);

  const startDeploy = async () => {
    setDeploying(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/build/${sessionId}/deploy/cloudflare`,
        { project_name: name.trim() },
        { withCredentials: true, timeout: 120000 },
      );
      setResult(r.data);
      toast.success('Live on Cloudflare Pages!');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Deploy failed. Please try again.';
      toast.error(msg);
      setResult({ ok: false, error: msg });
    } finally {
      setDeploying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="deploy-cloudflare-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-orange-500" /> Deploy live to the web
          </DialogTitle>
          <DialogDescription>
            Get a live URL on Cloudflare Pages in under 90 seconds — no GitHub, no build step.
          </DialogDescription>
        </DialogHeader>

        {!isAuthed ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Sign in first to save and deploy your builds.</span>
            </div>
            <Button onClick={onLogin} className="w-full h-9 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white">
              Sign in
            </Button>
          </div>
        ) : !hasProject ? (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">
            Generate something first — then come back here to ship it live.
          </div>
        ) : result?.ok ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 to-cyan-50 p-4">
              <div className="flex items-start gap-2 text-emerald-900">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <div className="font-semibold text-sm">Live on the web!</div>
                  <div className="text-xs text-emerald-700">Stage: {result.stage || 'deploying'} · this may take ~30s to be fully reachable</div>
                </div>
              </div>
              <div className="mt-3 p-2 bg-white border border-emerald-200 rounded-md font-mono text-xs text-neutral-700 truncate">
                {result.url}
              </div>
            </div>
            <a href={result.url} target="_blank" rel="noopener noreferrer" className="block w-full" data-testid="deploy-cf-open-link">
              <Button className="w-full h-10 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white">
                <ExternalLink className="w-4 h-4 mr-2" /> Open live site
              </Button>
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-neutral-700">Project name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="my-awesome-app"
                data-testid="deploy-cf-name-input"
                className="h-9 text-sm"
              />
              <p className="text-[11px] text-neutral-500">
                Live URL preview: <span className="font-mono text-neutral-700">{name || 'project'}-xxxxxx.pages.dev</span>
              </p>
            </div>

            <div className="rounded-lg border border-neutral-200 bg-gradient-to-br from-neutral-50 to-white p-3 text-xs text-neutral-600 space-y-1.5">
              <div className="flex items-center gap-1.5 text-neutral-900 font-medium">
                <Sparkles className="w-3.5 h-3.5 text-cyan-500" /> What we'll do
              </div>
              <ul className="space-y-1 ml-0">
                <li>• Bundle your React app into a single static HTML file</li>
                <li>• Create a Cloudflare Pages project</li>
                <li>• Push it live — global CDN, free HTTPS</li>
              </ul>
            </div>

            <Button
              onClick={startDeploy}
              disabled={deploying || !name.trim()}
              className="w-full h-10 rounded-lg bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white disabled:opacity-50"
              data-testid="deploy-cf-push-btn"
            >
              {deploying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deploying… (~60s)</> : <><Rocket className="w-4 h-4 mr-2" /> Deploy live</>}
            </Button>
            {result?.error && (
              <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2">{result.error}</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
